import { FC, useMemo } from 'react';
import { IOrderCategorySummary } from '../../../model/IOrderSummary';
import TimeSeriesChart from './TimeSeriesChart';

// --
// -- The by-date report, drawn.
// --
// -- Two charts rather than one with two scales. Orders are a count and the
// -- money is dollars; putting them on a shared y axis would invent a
// -- correlation between them that is not in the data.
// --
// -- Total and Gross are both dollars, so those two do share an axis, and the
// -- gap between the lines is the margin - which is the point of plotting them
// -- together.
// --
// -- Nothing here is the only way to read a number: the table underneath
// -- carries every value these lines summarise.
// --

/** 'yyyy-MM-dd' as a local date, so a timezone cannot shift it a day. */
const parseDay = (label: string): number => {
  const [y, m, d] = label.split('-').map(Number);
  return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
    ? new Date(y, m - 1, d).getTime()
    : NaN;
};

const countFormatter = new Intl.NumberFormat('en-US');

// Whole dollars: these are day totals in the thousands, so ".00" on every one
// is noise. The shared priceFormatter always prints cents, hence a local one.
const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0,
});

const money = (value: number) => moneyFormatter.format(Math.round(value));

/**
 * Money short enough for an axis tick.
 *
 * "$100,000.00" is wider than any sensible axis gutter - spelled out on every
 * tick it either overflows the chart or eats the plot.
 */
const compactMoney = (value: number): string => {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `${sign}$${Math.round(abs)}`;
};

const OrdersByDateCharts: FC<{
  rows?: IOrderCategorySummary[],
  isFetching?: boolean,
}> = ({ rows, isFetching = false }) => {
  // -- The report comes back newest first, which draws time backwards.
  const points = useMemo(() => (rows || [])
    .filter((row) => !!row.category && Number.isFinite(parseDay(row.category)))
    .map((row) => ({ row, at: parseDay(row.category) }))
    .sort((a, b) => a.at - b.at), [rows]);

  // Nothing to draw, and no empty padded frame where a chart would be. The
  // charts themselves stand aside below two points; this keeps the gap out of
  // the card too.
  if (!rows || points.length < 2) return null;

  const axis = points.map(({ row, at }) => ({ label: row.category, at }));

  return (
    <div className="flex flex-wrap gap-8 px-4 sm:px-6 lg:px-8 pt-5">
      <TimeSeriesChart
        title="Orders per day"
        points={axis}
        isFetching={isFetching}
        format={(value) => countFormatter.format(Math.round(value))}
        series={[{ name: 'Orders', values: points.map(({ row }) => row.ordersCount) }]}
      />
      <TimeSeriesChart
        title="Revenue per day"
        points={axis}
        isFetching={isFetching}
        format={money}
        formatTick={compactMoney}
        series={[
          { name: 'Total', values: points.map(({ row }) => row.totalPrice) },
          { name: 'Gross', values: points.map(({ row }) => row.grossAmount) },
        ]}
      />
    </div>
  );
};

export default OrdersByDateCharts;
